import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import useApiEp from "../../hooks/useApiEp";
import PermDataSettingOutlinedIcon from "@mui/icons-material/PermDataSettingOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import WebhookOutlinedIcon from "@mui/icons-material/WebhookOutlined";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserProvider";
import { useNavigate } from "react-router-dom";
import { getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { LoadingButton } from "@mui/lab";

const panelSx = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 3,
  boxShadow: "0 12px 32px rgba(15, 76, 68, 0.06)",
  overflow: "hidden",
};

const wabaFormPayloadExample = JSON.stringify(
  {
    event: "waba_forms.response",
    id: "wamid.HBgMOTE5XXXXXXXXFQIAERgSOD...",
    customerId: "your-customer-id",
    phoneNumberId: "123456789012345",
    from: "919876543210",
    profileName: "Customer name",
    flowToken: "your-flow-token",
    response: {
      flow_token: "your-flow-token",
      full_name: "Jane Doe",
      email: "jane@example.com",
      selected_option: "Request a callback",
    },
    receivedAt: "2026-09-24T10:30:00.000Z",
  },
  null,
  2
);

const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied to clipboard`);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error(`Unable to copy ${label.toLowerCase()}`);
    }
  };

  return (
    <Tooltip title={copied ? "Copied" : `Copy ${label.toLowerCase()}`}>
      <span>
        <IconButton
          onClick={handleCopy}
          disabled={!text}
          color={copied ? "success" : "default"}
          aria-label={`Copy ${label.toLowerCase()}`}
          size="small"
          sx={{ borderRadius: 1.5, bgcolor: "action.hover" }}
        >
          {copied ? (
            <CheckCircleOutlineIcon fontSize="small" />
          ) : (
            <ContentCopyOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

const SectionHeading = ({ icon, title, description }) => (
  <Stack direction="row" spacing={1.75} alignItems="flex-start">
    <Box
      sx={{
        display: "grid",
        placeItems: "center",
        width: 42,
        height: 42,
        flexShrink: 0,
        borderRadius: 2,
        bgcolor: "rgba(18, 140, 126, 0.10)",
        color: "primary.main",
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {description}
      </Typography>
    </Box>
  </Stack>
);

const ApiSettings = () => {
  const navigate = useNavigate();
  const [apiToken, setApiToken] = useState("");
  const [apiCode, setApiCode] = useState("");
  const [campaignEp, setCampaignEp] = useState("");
  const [sessionEp, setSessionEp] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [wabaFormWebhook, setWabaFormWebhook] = useState("");
  const [wabaFormsWebhookSecret, setWabaFormsWebhookSecret] = useState("");
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [verifyingWebhook, setVerifyingWebhook] = useState(false);
  const [sendingSampleWebhook, setSendingSampleWebhook] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState(null);
  const { user } = useContext(UserContext);
  const [generating, setGenerating] = useState(false);
  const [submittingCode, setSubmittingCode] = useState(false);
  const {
    data: apiCred,
    isLoading,
    isError,
    error,
    refetch,
  } = useApiEp(`/user/get-api-cred`);
  const {
    data: webhookSettings,
    isLoading: isWebhookLoading,
    isError: isWebhookError,
    error: webhookError,
    refetch: refetchWebhookSettings,
  } = useApiEp(`/send/save-webhook-waba-forms`);

  useEffect(() => {
    if (apiCred) {
      setApiToken(apiCred.apiToken);
      setApiCode(apiCred.apiCode);
      setCampaignEp(apiCred?.endpoint?.campaign ?? "");
      setSessionEp(apiCred?.endpoint?.session ?? "");
    }
  }, [apiCred]);

  useEffect(() => {
    if (webhookSettings) {
      setWabaFormWebhook(webhookSettings.wabaFormWebhook ?? "");
      setWabaFormsWebhookSecret(
        webhookSettings.wabaFormsWebhookSecret ?? ""
      );
    }
  }, [webhookSettings]);

  const generateNewToken = async () => {
    try {
      setGenerating(true);
      const response = await axios.patch(
        `${API_ENDPOINT}/user/update-api-token`,
        {},
        getHeader()
      );
      toast.success(response.data.message);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setGenerating(false);
    }
  };

  const saveToken = async () => {
    if (!apiCode) {
      toast.error("Please enter code first!");
      return;
    }
    try {
      setSubmittingCode(true);
      const response = await axios.patch(
        `${API_ENDPOINT}/user/update-api-code`,
        { code: apiCode },
        getHeader()
      );
      toast.success(response.data.message);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setSubmittingCode(false);
    }
  };

  const saveWabaFormsWebhook = async () => {
    const webhook = wabaFormWebhook.trim();
    if (!webhook) {
      toast.error("Please enter a webhook URL");
      return;
    }

    try {
      const parsedWebhook = new URL(webhook);
      if (!["http:", "https:"].includes(parsedWebhook.protocol)) {
        throw new Error("Unsupported protocol");
      }
    } catch (error) {
      toast.error("Please enter a valid HTTP or HTTPS webhook URL");
      return;
    }

    try {
      setSavingWebhook(true);
      const response = await axios.post(
        `${API_ENDPOINT}/send/save-webhook-waba-forms`,
        {
          wabaFormWebhook: webhook,
          wabaFormsWebhookSecret,
        },
        getHeader()
      );
      toast.success(
        response.data.message || "Webhook verified and saved successfully"
      );
      await refetchWebhookSettings();
    } catch (error) {
      const message = error.response?.data?.message;
      if (message) {
        toast.error(message);
      } else {
        toastError(error);
      }
    } finally {
      setSavingWebhook(false);
    }
  };

  const runWebhookTest = async (path, setLoading) => {
    const webhook = wabaFormWebhook.trim();
    if (!webhook) {
      toast.error("Please enter a webhook URL");
      return;
    }

    try {
      const parsedWebhook = new URL(webhook);
      if (!["http:", "https:"].includes(parsedWebhook.protocol)) {
        throw new Error("Unsupported protocol");
      }
    } catch (error) {
      toast.error("Please enter a valid HTTP or HTTPS webhook URL");
      return;
    }

    try {
      setLoading(true);
      setWebhookTestResult(null);
      const response = await axios.post(
        `${API_ENDPOINT}${path}`,
        {
          wabaFormWebhook: webhook,
          wabaFormsWebhookSecret,
        },
        getHeader()
      );
      setWebhookTestResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      const result = error.response?.data ?? {
        success: false,
        message: "The webhook request could not be completed.",
      };
      setWebhookTestResult(result);
      toast.error(result.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyWabaFormsWebhook = () =>
    runWebhookTest(
      "/send/verify-webhook-waba-forms",
      setVerifyingWebhook
    );

  const sendSampleWabaFormsWebhook = () =>
    runWebhookTest(
      "/send/test-webhook-waba-forms",
      setSendingSampleWebhook
    );

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "background.paper",
      borderRadius: 2,
    },
  };

  const endpoints = [
    {
      label: "Campaign endpoint",
      value: campaignEp,
      path: "/home/campaign-api-docs",
    },
    {
      label: "Session endpoint",
      value: sessionEp,
      path: "/home/session-api-docs",
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 4 }, pb: 7 }}>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          mb: 3,
          p: { xs: 2.5, sm: 3.5 },
          borderRadius: 3,
          color: "common.white",
          background:
            "linear-gradient(125deg, #075E54 0%, #128C7E 58%, #1FAF77 100%)",
          boxShadow: "0 16px 38px rgba(7, 94, 84, 0.20)",
          "&::after": {
            content: '""',
            position: "absolute",
            width: 220,
            height: 220,
            right: -70,
            top: -100,
            borderRadius: "50%",
            bgcolor: "rgba(255, 255, 255, 0.08)",
          },
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2.5}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Box>
            <Typography
              component="h1"
              sx={{ fontSize: { xs: 24, sm: 30 }, fontWeight: 750 }}
            >
              API settings
            </Typography>
            <Typography sx={{ mt: 0.75, opacity: 0.82, maxWidth: 620 }}>
              Manage the credentials and endpoints used to connect your
              applications securely.
            </Typography>
          </Box>
          <Chip
            icon={<BadgeOutlinedIcon />}
            label={`Customer ID: ${user?.customerId || "—"}`}
            sx={{
              alignSelf: { xs: "flex-start", sm: "center" },
              height: 38,
              px: 0.75,
              color: "common.white",
              bgcolor: "rgba(255, 255, 255, 0.14)",
              border: "1px solid rgba(255, 255, 255, 0.22)",
              "& .MuiChip-icon": { color: "inherit" },
            }}
          />
        </Stack>
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error?.message || "Failed to load your API credentials."}
        </Alert>
      )}

      <Stack spacing={3}>
        <Card sx={panelSx} elevation={0}>
          <Box
            sx={{
              p: { xs: 2.5, sm: 3 },
              bgcolor: "rgba(18, 140, 126, 0.025)",
            }}
          >
            <SectionHeading
              icon={<KeyOutlinedIcon />}
              title="API credentials"
              description="Use these values to authenticate requests from your application."
            />
          </Box>
          <CardContent
            sx={{
              p: { xs: 2.5, sm: 3 },
              "&:last-child": { pb: { xs: 2.5, sm: 3 } },
            }}
          >
            {isLoading ? (
              <Stack spacing={2.5}>
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="rounded" height={56} />
              </Stack>
            ) : (
              <Stack spacing={3}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  alignItems={{ md: "flex-start" }}
                >
                  <TextField
                    fullWidth
                    label="API token"
                    type={showToken ? "text" : "password"}
                    value={apiToken}
                    helperText="Valid for one year. Generating a new token immediately replaces the current one."
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={showToken ? "Hide token" : "Show token"}>
                            <IconButton
                              onClick={() =>
                                setShowToken((visible) => !visible)
                              }
                              edge="end"
                              aria-label={
                                showToken ? "Hide API token" : "Show API token"
                              }
                            >
                              {showToken ? (
                                <VisibilityOffOutlinedIcon fontSize="small" />
                              ) : (
                                <VisibilityOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <CopyButton text={apiToken} label="API Token" />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />
                  <LoadingButton
                    startIcon={<PermDataSettingOutlinedIcon />}
                    onClick={generateNewToken}
                    variant="contained"
                    loading={generating}
                    sx={{
                      minWidth: { md: 210 },
                      height: 56,
                      whiteSpace: "nowrap",
                      alignSelf: { xs: "stretch", md: "flex-start" },
                    }}
                  >
                    Generate new token
                  </LoadingButton>
                </Stack>

                <Box
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                    pt: 3,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    alignItems="stretch"
                  >
                    <TextField
                      fullWidth
                      label="API code"
                      value={apiCode}
                      onChange={(event) => setApiCode(event.target.value)}
                      helperText="Choose a private code that is easy for your system to identify."
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CopyButton text={apiCode} label="API Code" />
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    />
                    <LoadingButton
                      startIcon={<SaveOutlinedIcon />}
                      onClick={saveToken}
                      variant="outlined"
                      loading={submittingCode}
                      sx={{
                        minWidth: { md: 210 },
                        height: 56,
                        whiteSpace: "nowrap",
                        alignSelf: { xs: "stretch", md: "flex-start" },
                      }}
                    >
                      Save API code
                    </LoadingButton>
                  </Stack>
                </Box>
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card sx={panelSx} elevation={0}>
          <Box
            sx={{
              p: { xs: 2.5, sm: 3 },
              bgcolor: "rgba(52, 183, 241, 0.035)",
            }}
          >
            <SectionHeading
              icon={<HubOutlinedIcon />}
              title="API endpoints"
              description="Copy a production endpoint or open its integration guide."
            />
          </Box>
          <CardContent
            sx={{
              p: { xs: 2.5, sm: 3 },
              "&:last-child": { pb: { xs: 2.5, sm: 3 } },
            }}
          >
            {isLoading ? (
              <Stack spacing={2.5}>
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="rounded" height={56} />
              </Stack>
            ) : (
              <Stack spacing={2.5}>
                {endpoints.map((endpoint, index) => (
                  <Box
                    key={endpoint.label}
                    sx={
                      index
                        ? {
                            borderTop: "1px solid",
                            borderColor: "divider",
                            pt: 2.5,
                          }
                        : undefined
                    }
                  >
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1.5}
                      alignItems="stretch"
                    >
                      <TextField
                        fullWidth
                        label={endpoint.label}
                        value={endpoint.value}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <CopyButton
                                text={endpoint.value}
                                label={endpoint.label}
                              />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          ...fieldSx,
                          "& input": {
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, monospace",
                            fontSize: "0.875rem",
                          },
                        }}
                      />
                      <Button
                        onClick={() => navigate(endpoint.path)}
                        variant="text"
                        endIcon={<ArrowForwardRoundedIcon />}
                        sx={{
                          minWidth: { md: 210 },
                          height: 56,
                          justifyContent: {
                            xs: "center",
                            md: "space-between",
                          },
                          px: 2,
                          bgcolor: "rgba(18, 140, 126, 0.06)",
                          "&:hover": {
                            bgcolor: "rgba(18, 140, 126, 0.12)",
                          },
                        }}
                      >
                        View documentation
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card sx={panelSx} elevation={0}>
          <Box
            sx={{
              p: { xs: 2.5, sm: 3 },
              bgcolor: "rgba(124, 77, 255, 0.035)",
            }}
          >
            <SectionHeading
              icon={<WebhookOutlinedIcon />}
              title="WABA forms webhook"
              description="Send WABA form submissions to your application as they arrive."
            />
          </Box>
          <CardContent
            sx={{
              p: { xs: 2.5, sm: 3 },
              "&:last-child": { pb: { xs: 2.5, sm: 3 } },
            }}
          >
            {isWebhookError && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                {webhookError?.message || "Failed to load webhook settings."}
              </Alert>
            )}

            {isWebhookLoading ? (
              <Stack spacing={2.5}>
                <Skeleton variant="rounded" height={56} />
                <Skeleton variant="rounded" height={56} />
              </Stack>
            ) : (
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  required
                  label="Webhook URL"
                  type="url"
                  value={wabaFormWebhook}
                  onChange={(event) => {
                    setWabaFormWebhook(event.target.value);
                    setWebhookTestResult(null);
                  }}
                  placeholder="https://example.com/webhooks/waba-forms"
                  helperText="The endpoint must accept a verification POST and return a 2xx response within 8 seconds."
                  sx={fieldSx}
                />
                <TextField
                  fullWidth
                  label="Webhook secret (optional)"
                  type={showWebhookSecret ? "text" : "password"}
                  value={wabaFormsWebhookSecret}
                  onChange={(event) => {
                    setWabaFormsWebhookSecret(event.target.value);
                    setWebhookTestResult(null);
                  }}
                  helperText="When set, it is included in the X-WABA-Webhook-Secret header during verification."
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={
                            showWebhookSecret ? "Hide secret" : "Show secret"
                          }
                        >
                          <IconButton
                            onClick={() =>
                              setShowWebhookSecret((visible) => !visible)
                            }
                            edge="end"
                            aria-label={
                              showWebhookSecret
                                ? "Hide webhook secret"
                                : "Show webhook secret"
                            }
                          >
                            {showWebhookSecret ? (
                              <VisibilityOffOutlinedIcon fontSize="small" />
                            ) : (
                              <VisibilityOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  sx={fieldSx}
                />
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                >
                  <LoadingButton
                    startIcon={<SaveOutlinedIcon />}
                    onClick={saveWabaFormsWebhook}
                    variant="contained"
                    loading={savingWebhook}
                    disabled={verifyingWebhook || sendingSampleWebhook}
                  >
                    Verify and save webhook
                  </LoadingButton>
                  <LoadingButton
                    onClick={verifyWabaFormsWebhook}
                    variant="outlined"
                    loading={verifyingWebhook}
                    disabled={savingWebhook || sendingSampleWebhook}
                  >
                    Verify without saving
                  </LoadingButton>
                </Stack>

                {webhookTestResult && (
                  <Alert
                    severity={webhookTestResult.success ? "success" : "error"}
                    sx={{ borderRadius: 2 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {webhookTestResult.message}
                    </Typography>
                    {webhookTestResult.data && (
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          mt: 1.5,
                          p: 1.5,
                          maxHeight: 280,
                          overflow: "auto",
                          borderRadius: 1.5,
                          bgcolor: "rgba(0, 0, 0, 0.06)",
                          color: "text.primary",
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                          fontSize: "0.75rem",
                        }}
                      >
                        {JSON.stringify(webhookTestResult.data, null, 2)}
                      </Box>
                    )}
                  </Alert>
                )}

                <Box
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "divider",
                    pt: 3,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="flex-start"
                    justifyContent="space-between"
                    sx={{ mb: 1.5 }}
                  >
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Form response payload
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your endpoint receives an HTTP POST with a JSON body for
                        every submitted WABA form.
                      </Typography>
                    </Box>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ sm: "center" }}
                    >
                      <LoadingButton
                        size="small"
                        variant="outlined"
                        onClick={sendSampleWabaFormsWebhook}
                        loading={sendingSampleWebhook}
                        disabled={savingWebhook || verifyingWebhook}
                      >
                        Send sample payload
                      </LoadingButton>
                      <CopyButton
                        text={wabaFormPayloadExample}
                        label="Payload example"
                      />
                    </Stack>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mb: 1.5 }}
                  >
                    <Chip size="small" label="POST" color="primary" />
                    <Chip size="small" label="application/json" />
                    <Chip size="small" label="Event: waba_forms.response" />
                  </Stack>

                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 2,
                      maxHeight: 420,
                      overflow: "auto",
                      borderRadius: 2,
                      bgcolor: "grey.900",
                      color: "grey.100",
                      fontFamily:
                        "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      fontSize: "0.8rem",
                      lineHeight: 1.6,
                    }}
                  >
                    {wabaFormPayloadExample}
                  </Box>

                  <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                    If a secret is configured, it is sent in the
                    <Box
                      component="code"
                      sx={{ mx: 0.75, fontWeight: 700 }}
                    >
                      X-WABA-Webhook-Secret
                    </Box>
                    header. Return a 2xx response within 8 seconds. Use
                    <Box
                      component="code"
                      sx={{ mx: 0.75, fontWeight: 700 }}
                    >
                      id
                    </Box>
                    as the idempotency key because failed deliveries may be
                    retried.
                  </Alert>
                </Box>
              </Stack>
            )}
          </CardContent>
        </Card>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          color="text.secondary"
          sx={{ px: 2 }}
        >
          <ShieldOutlinedIcon fontSize="small" color="primary" />
          <Typography variant="caption" textAlign="center">
            Keep your credentials private and rotate your token if you suspect
            it has been exposed.
          </Typography>
          <Tooltip title="Credentials grant access to your account. Never include them in public code or client-side applications.">
            <InfoOutlinedIcon fontSize="inherit" sx={{ cursor: "help" }} />
          </Tooltip>
        </Stack>
      </Stack>
    </Container>
  );
};

export default ApiSettings;
